/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Global authorization context provider.
 * Fetches the user's role ONCE and shares it with all useAuthorization() consumers.
 * Eliminates redundant /api/users/me/role calls from 31+ independent hook instances.
 */

"use client";

import {
  normalizeRole,
  permissionsForRole,
  type AppRole,
} from "@/lib/business-logic/organizations/authorization";
import { useUserSafe as useUser } from "@/hooks/auth/use-user-safe";
import { detectClientSideTestBypass } from "@/lib/testing/test-mode";
import { captureMessage } from "@/lib/monitoring/sentry";
import { logger } from "@/lib/utils/logger";
import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface AuthorizationState {
  role: AppRole;
  canManageOrganization: boolean;
  canManageUsers: boolean;
  canManageData: boolean;
  canViewSensitive: boolean;
  canManageTeam: boolean;
  canManageFieldOps: boolean;
  isLoading: boolean;
}

const AuthorizationContext = createContext<AuthorizationState | null>(null);

const TEST_MODE_ACTIVE =
  process.env.NEXT_PUBLIC_E2E_TEST_MODE === "true" ||
  process.env.E2E_TEST_MODE === "true";

const testModeRole = normalizeRole(
  process.env.NEXT_PUBLIC_TEST_USER_ROLE ??
    process.env.TEST_USER_ROLE ??
    "OWNER",
);

export function AuthorizationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isLoaded } = useUser();

  const testBypass = useMemo(() => {
    if (typeof window === "undefined") {
      return { isActive: false } as const;
    }
    try {
      return detectClientSideTestBypass();
    } catch (error) {
      logger.warn(
        "[AuthorizationProvider] Failed to detect test bypass, defaulting to inactive",
        {
          error: error instanceof Error ? error.message : String(error),
        },
      );
      return { isActive: false } as const;
    }
  }, []);

  const isTestMode = TEST_MODE_ACTIVE || testBypass.isActive;

  const initialRole = useMemo(() => {
    if (typeof window === "undefined") {
      return "VIEWER" as AppRole;
    }
    if (testBypass.isActive && "user" in testBypass && testBypass.user) {
      return normalizeRole(testBypass.user.role);
    }
    return "VIEWER" as AppRole;
  }, [testBypass]);

  const [role, setRole] = useState<AppRole>(initialRole);
  const [dbRoleFallback, setDbRoleFallback] = useState<AppRole | null>(null);
  const [dbFetchDone, setDbFetchDone] = useState(false);

  const [isHydrated, setIsHydrated] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return testBypass.isActive;
  });

  // Track user+org combo to re-fetch when either changes
  const userOrgKey = useMemo(() => {
    if (!user) return null;
    const orgId =
      user.organizationMemberships?.[0]?.organization?.id ?? "no-org";
    return `${user.id}:${orgId}`;
  }, [user]);

  useLayoutEffect(() => {
    if (!isHydrated) {
      setIsHydrated(true);
    }

    if (testBypass.isActive && "user" in testBypass && testBypass.user) {
      setRole(normalizeRole(testBypass.user.role));
      setDbRoleFallback(null);
      setDbFetchDone(false);
    } else {
      const clerkRole = normalizeRole(user?.publicMetadata?.["role"]);
      setRole(clerkRole);
      if (clerkRole !== "VIEWER") {
        setDbRoleFallback(null);
        setDbFetchDone(false);
      }
    }
  }, [user, isLoaded, testBypass, isHydrated]);

  // Fetch role from DB once when Clerk role is VIEWER (missing/invalid).
  // Re-fetch when user or org changes by tracking the previous userOrgKey.
  const [lastFetchedKey, setLastFetchedKey] = useState<string | null>(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;

  useLayoutEffect(() => {
    if (
      typeof window === "undefined" ||
      testBypass.isActive ||
      !user?.id ||
      !isLoaded ||
      role !== "VIEWER"
    ) {
      return;
    }

    // If user/org changed, reset fallback so we re-fetch for new context
    if (userOrgKey !== lastFetchedKey) {
      setDbRoleFallback(null);
      retryCountRef.current = 0;
    } else if (dbFetchDone) {
      // Same user/org and already fetched — skip
      return;
    }

    setDbFetchDone(true);
    setLastFetchedKey(userOrgKey);
    fetch("/api/users/me/role", { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { data?: { role?: string } } | null) => {
        const r = body?.data?.role ?? null;
        if (r && r !== "VIEWER") {
          setDbRoleFallback(normalizeRole(r) as AppRole);
        }
        retryCountRef.current = 0;
      })
      .catch((err: unknown) => {
        if (retryCountRef.current < MAX_RETRIES) {
          const attempt = retryCountRef.current;
          retryCountRef.current += 1;
          const delay = Math.min(1000 * 2 ** attempt, 8000);
          logger.warn(
            "[AuthorizationProvider] Role fetch failed, scheduling retry (non-blocking)",
            {
              operation: "AUTH_PROVIDER_ROLE_FETCH_RETRY",
              attempt: attempt + 1,
              maxRetries: MAX_RETRIES,
              delayMs: delay,
              error: err instanceof Error ? err.message : String(err),
            },
          );
          void captureMessage(
            "[AuthorizationProvider] Role fetch failed, scheduling retry (non-blocking)",
            {
              level: "warning",
              tags: {
                swallow: "intentional_non_blocking",
                operation: "AUTH_PROVIDER_ROLE_FETCH_RETRY",
                surface: "authorization_provider",
              },
            },
          );
          setTimeout(() => {
            setDbFetchDone(false);
            setLastFetchedKey(null);
          }, delay);
        } else {
          // Max retries exceeded — fall back to Clerk role
          logger.warn(
            "[AuthorizationProvider] Role fetch failed after max retries, falling back to Clerk role",
            {
              operation: "AUTH_PROVIDER_ROLE_FETCH_EXHAUSTED",
              maxRetries: MAX_RETRIES,
              error: err instanceof Error ? err.message : String(err),
            },
          );
          void captureMessage(
            "[AuthorizationProvider] Role fetch exhausted retries (non-blocking)",
            {
              level: "warning",
              tags: {
                swallow: "intentional_non_blocking",
                operation: "AUTH_PROVIDER_ROLE_FETCH_EXHAUSTED",
                surface: "authorization_provider",
              },
            },
          );
          setDbRoleFallback(null);
        }
      });
  }, [
    user?.id,
    isLoaded,
    role,
    testBypass.isActive,
    userOrgKey,
    lastFetchedKey,
    dbFetchDone,
  ]);

  const finalRole = isHydrated
    ? role === "VIEWER" && dbRoleFallback
      ? dbRoleFallback
      : role
    : typeof window !== "undefined" && testBypass.isActive
      ? initialRole
      : "VIEWER";

  const value = useMemo<AuthorizationState>(() => {
    if (isTestMode) {
      const testRole =
        testBypass.isActive && "user" in testBypass && testBypass.user
          ? normalizeRole(testBypass.user.role)
          : testModeRole;
      const effectiveRole = testRole || finalRole;
      return {
        role: effectiveRole,
        ...permissionsForRole(effectiveRole),
        isLoading: false,
      };
    }

    const isLoading = !isHydrated || (!testBypass.isActive && !isLoaded);

    return {
      role: finalRole,
      ...permissionsForRole(finalRole),
      isLoading,
    };
  }, [finalRole, isHydrated, isLoaded, isTestMode, testBypass]);

  return (
    <AuthorizationContext.Provider value={value}>
      {children}
    </AuthorizationContext.Provider>
  );
}

export function useAuthorizationContext(): AuthorizationState {
  const ctx = useContext(AuthorizationContext);
  if (!ctx) {
    throw new Error(
      "useAuthorizationContext must be used within an AuthorizationProvider",
    );
  }
  return ctx;
}
